import { AmorgAdminToken, AmorgAdminUserListing, ApplicationFeatures, GlobalSurveyTemplate, LoggedEmail, Partner, PublicAmorgUser, CompanyInfo } from '../amorg-common';
import { Site, SiteSettings } from '../amorg-common';
import { appUrlBuilder } from '../utils/appUrlBuilder';
import { getAuthorizedActiveUserOrRedirect } from './serverApiHelpers';

export const apiPath = '/api';
export const amorgAdminPath = `${apiPath}/amorgy`;

export async function getEmails (siteId: string, onNoAccess: () => void): Promise<LoggedEmail[]>
{
    const response = await fetch(`${amorgAdminPath}/email-log/${siteId}`);
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return response.json();
    else
        throw new Error('Unhandled result');
}

export async function getAllSites (onNoAccess: () => void): Promise<Site[]>
{
    const response = await fetch(`${amorgAdminPath}/sites`);
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
    {
        const siteList = await response.json() as Site[];
        siteList.sort((lhs, rhs) => lhs.id.localeCompare(rhs.id));
        return siteList;
    }
    else
        throw new Error('Unhandled result');
}

export async function getSiteUsers (siteId: string, onNoAccess: () => void): Promise<PublicAmorgUser[]>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/users`);
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
    {
        const users = await response.json() as PublicAmorgUser[];
        users.sort((lhs, rhs) => lhs.email.localeCompare(rhs.email));
        return users;
    }
    else
        throw new Error('Unhandled result');
}

export async function impersonate (siteId: string, email: string): Promise<{ landingUrl:string }>
{
    const response = await fetch(`${amorgAdminPath}/sites/${siteId}/users/${email}/impersonate`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        return { landingUrl: '/amorgy/login' };
    else if (response.status === 200)
    {
        const user = await getAuthorizedActiveUserOrRedirect([]);
        return { landingUrl: appUrlBuilder.home.build(user) };
    }
    else
        return { landingUrl: '/amorgy/login' };
}

export async function createSite (siteId: string, onNoAccess: () => void): Promise<boolean>
{
    const response = await fetch(`/api/amorgy/sites`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"siteId":"${siteId}"}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
    {
        return true;
    }
    else
        return false;
}

export async function updateSiteSettings (siteId: string, settings: SiteSettings, companyInfo: CompanyInfo, partner: Partner | null, loginName:string, onNoAccess: () => void)
{
    settings.features = settings.features.filter(f => ApplicationFeatures.includes(f));

    const response = await fetch(`/api/amorgy/sites/${siteId}`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ ...settings, ...companyInfo, partner, loginName })
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return;
    else
        throw new Error('Unhandled result');
}

export async function getGlobalSurveyTemplates (onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/global-templates`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return (await response.json()) as GlobalSurveyTemplate[];
    else
        throw new Error('Unhandled result');
}

export async function importGlobalSurveyTemplate (
    siteId: string,
    siteTemplateId: string,
    globalTemplateId: string,
    category: string,
    onNoAccess: () => void
)
{
    const body = JSON.stringify({ siteId, siteSurveyTemplateId: siteTemplateId, category });
    const response = await fetch(`/api/amorgy/global-templates/${globalTemplateId}`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function editGlobalSurveyTemplate (
    template: GlobalSurveyTemplate,
    onNoAccess: () => void
)
{
    const body = JSON.stringify(template);
    const response = await fetch(`/api/amorgy/global-templates`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function getSiteSurveyTemplates (siteId: string, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/site-templates/${siteId}`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return (await response.json()) as GlobalSurveyTemplate[];
    else
        throw new Error('Unhandled result');
}

export async function login (email: string, password: string)
{
    const response = await fetch(`/api/amorgy/login`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"email":"${email}", "password":"${password}"}`
        });
    return response.status === 200;
}

export async function logout ()
{
    const response = await fetch(`/api/amorgy/logout`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    return response.status === 200;
}

export async function getUsers (onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/users`,
        {
            method: 'GET',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' }
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return (await response.json()) as AmorgAdminUserListing[];
    else
        throw new Error('Unhandled result');
}

export async function createUser (email: string, password: string, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user`,
        {
            method: 'POST',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"email":"${email}", "password":"${password}"}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function changeUserPassword (userId: string, password: string, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user/${userId}/password`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"password":"${password}"}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function changeUserAccess (userId: string, enabled: boolean, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user/${userId}/access`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: `{"enabled":${enabled}}`
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function changeUserPartner (userId: string, partner: Partner | null, onNoAccess: () => void)
{
    const response = await fetch(`/api/amorgy/user/${userId}/partner`,
        {
            method: 'PUT',
            cache: 'no-cache',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ partner })
        });
    if (response.status === 401)
        onNoAccess();
    else if (response.status === 200)
        return true;
    else
        return false;
}

export async function getActiveUser (onNoAccess: () => void)
{
    const result = await fetch(`/api/amorgy/activeuser`);
    if (result.status !== 200)
        onNoAccess();

    const token = await result.json();
    return token as AmorgAdminToken;
}